import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, ShieldCheck, UserCircle2, MessageCircle, Award, Lock, BadgeCheck, Share2 } from "lucide-react";
import { trackWhatsApp } from "../lib/beacon";
import { shareProfessionalCard } from "../lib/shareCard";
import QuickTriageModal from "../components/triage/QuickTriageModal";

const SUPPORT_WA = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";
const digits = (s) => (s || "").replace(/[^0-9]/g, "");

const TIER_STYLES = {
  ouro:   { label: "Ouro",   ring: "ring-[#D4A017]", bg: "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" },
  prata:  { label: "Prata",  ring: "ring-slate-400", bg: "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" },
  bronze: { label: "Bronze", ring: "ring-amber-700", bg: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white" },
};

/**
 * Perfil público. NÃO tem formulário de reserva — o pagamento antecipado é
 * mediado pelo consultor de plantão. Painel lateral direito mostra selo,
 * credencial e CTA "Falar com consultor" (WhatsApp).
 */
const PublicProfessionalDetail = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onCall, setOnCall] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [triageOpen, setTriageOpen] = useState(false);
  const [convenios, setConvenios] = useState([]);

  useEffect(() => {
    api.get(`/professionals/${id}`).then(r => setP(r.data))
      .catch(() => toast.error("Profissional não encontrado"))
      .finally(() => setLoading(false));
    api.get("/public/on-call").then(r => setOnCall(r.data)).catch(() => {});
    // Convênios ativos — regra absoluta: só entra aqui quem tem credential status=active
    api.get(`/convenios/professionals/${id}/credentials`).then(r => setConvenios(r.data)).catch(() => {});
  }, [id]);

  const onShare = async () => {
    if (sharing || !p) return;
    setSharing(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const profileUrl = `${window.location.origin}/profissionais/${p.id}`;
      const res = await shareProfessionalCard({
        apiBase, professionalId: p.id,
        displayName: p.display_name, profileUrl,
        whatsappUrl: waUrl,
      });
      if (res.mode === "shared") toast.success("Card compartilhado.");
      else if (res.mode === "downloaded-and-copied") toast.success("Imagem baixada e texto com link do consultor copiado — cole no Instagram/LinkedIn.");
      else if (res.mode === "downloaded") toast.success("Imagem baixada. Poste onde quiser!");
      else if (res.mode === "cancelled") { /* silêncio */ }
    } catch (e) {
      toast.error(e?.message || "Não foi possível gerar o card agora.");
    } finally { setSharing(false); }
  };

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;
  if (!p) return <div className="p-10">Profissional não encontrado. <Link to="/profissionais" className="text-emerald-700 underline">Voltar</Link></div>;

  const tier = (p.certification_tier || "").toLowerCase();
  const style = TIER_STYLES[tier];
  const target = digits(onCall?.online ? (onCall.phone || SUPPORT_WA) : SUPPORT_WA);
  const consultorName = onCall?.online && onCall?.first_name ? onCall.first_name : "consultor RAF";
  const msg = `Olá, ${consultorName}. Vi o perfil da(o) ${p.display_name || "profissional"} na RAF Direct e gostaria de agendar uma consulta.`;
  const waUrl = `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="prof-detail-page">
      <Link to="/profissionais" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Todos os profissionais
      </Link>

      <div className="mt-4 grid md:grid-cols-3 gap-8 items-start">
        {/* Coluna esquerda — perfil */}
        <div className="md:col-span-2 text-left">
          <div className="flex items-start gap-5">
            {p.photo_url ? (
              <img src={p.photo_url} alt=""
                className={`w-24 h-24 rounded-full object-cover border-4 border-white shadow-md ${style ? `ring-4 ring-offset-2 ${style.ring}` : ""}`} />
            ) : (
              <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><UserCircle2 className="w-10 h-10" /></div>
            )}
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> {p.council_type} · {p.council_state}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <h1 className="font-display font-bold text-[#0B2B62] text-2xl md:text-3xl text-left">{p.display_name}</h1>
                <span
                  title="Perfil auditado e aprovado pela curadoria RAF Direct — documentos, conselho de classe e biografia validados."
                  data-testid="prof-verified-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-600 text-white ring-1 ring-emerald-700 shadow-sm">
                  <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Perfil Verificado
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(p.categories || []).map(c => (
                  <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{c}</span>
                ))}
              </div>
            </div>
          </div>

          {p.bio && (
            <div className="mt-6 text-left">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg text-left">Sobre</h2>
              <p className="mt-2 text-slate-700 whitespace-pre-line leading-relaxed text-justify hyphens-auto">{p.bio}</p>
            </div>
          )}
          {convenios.length > 0 && (
            <div className="mt-6 text-left" data-testid="prof-convenios-block">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg text-left inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Convênios aceitos para consulta
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {convenios.map(c => (
                  <span key={c.insurer_id} data-testid={`prof-convenio-${c.insurer_slug}`}
                    title={c.teleconsultation_allowed ? "Teleconsulta autorizada" : "Autorização por consulta"}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                    <BadgeCheck className="w-3.5 h-3.5" /> {c.insurer_name}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Cobertura e autorização são validadas pelo consultor RAF antes do agendamento.
              </p>
            </div>
          )}
          {p.curriculum_text && (
            <div className="mt-6 text-left">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg text-left">Formação & experiência</h2>
              <p className="mt-2 text-slate-700 whitespace-pre-line leading-relaxed text-justify hyphens-auto">{p.curriculum_text}</p>
            </div>
          )}
        </div>

        {/* Coluna direita — Falar com consultor (alinhamento text-left match) */}
        <aside className="md:sticky md:top-24 h-fit bg-white border border-slate-200 rounded-xl p-5 shadow-sm text-left">
          {style && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${style.bg}`}>
              <Award className="w-3 h-3" /> Selo {style.label} · Validado RAF
            </span>
          )}
          <h3 className="mt-3 font-display font-bold text-[#0B2B62] text-lg text-left">Agendamento pelo consultor</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed text-left">
            O agendamento e o pagamento acontecem pelo <b>consultor RAF de plantão</b>. Ele valida horário, envia o link seguro de pagamento e libera a sala de vídeo. Se algo der errado, você tem até <b>3 dias</b> após o horário para pedir reembolso — o estorno cai em <b>72h úteis</b> a partir da solicitação.
          </p>

          <button type="button" onClick={() => setTriageOpen(true)}
            data-testid="wa-consultant-btn"
            className="mt-4 w-full inline-flex items-center justify-start gap-2 px-4 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm text-left">
            <MessageCircle className="w-4 h-4 shrink-0" /> Falar com consultor no WhatsApp
          </button>
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            data-testid="wa-consultant-direct"
            onClick={() => trackWhatsApp("public_prof_detail_direct", { prof_id: p.id })}
            className="mt-1 block text-center text-[11px] text-slate-500 hover:text-emerald-700 underline">
            Prefiro falar direto sem triagem
          </a>

          <button type="button" onClick={onShare} disabled={sharing}
            data-testid="share-prof-btn"
            title="Baixa uma imagem 1080x1080 do seu perfil verificado — pronta pro Instagram, LinkedIn e WhatsApp."
            className="mt-2 w-full inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-md border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 font-bold text-sm disabled:opacity-50 text-left">
            <Share2 className="w-4 h-4" />
            {sharing ? "Gerando imagem…" : "Compartilhar meu perfil"}
          </button>

          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-[11px] text-slate-500 text-left">
            <div className="flex items-start gap-1.5">
              <Lock className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Pagamento antecipado pelo Stripe (cartão ou PIX), centralizado na RAF — nunca por PIX pessoal.</span>
            </div>
            <div className="flex items-start gap-1.5">
              <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
              <span><b>Reembolso:</b> solicite em até <b>3 dias</b> após o horário agendado; o estorno é processado em até <b>72h úteis</b> a partir da solicitação.</span>
            </div>
          </div>
        </aside>
      </div>
      <QuickTriageModal open={triageOpen} onClose={() => setTriageOpen(false)}
        targetKind="prof" targetId={p.id} targetLabel={p.display_name} />
    </div>
  );
};

export default PublicProfessionalDetail;
